import React from "react";
import PropTypes from "prop-types";
import { Zap, Droplet, CheckCircle } from "lucide-react";
import styles from "./FuelSelector.module.css";
import { useLocation } from "react-router-dom";

const FuelSelector = ({ selectedFuel, onFuelChange }) => {
  const location = useLocation();

  const handleFuelChange = (fuelType, event) => {
    if (selectedFuel === location.state?.formData?.selectedFuel) {
      //set formdata null
      location.state.formData = null;
      sessionStorage.removeItem("navigated");
    }
    event.preventDefault();
    event.stopPropagation();

    // Store current scroll position
    const currentScrollY = window.scrollY;

    onFuelChange(fuelType);

    // Restore scroll position after state change
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });

    // Announce selection to screen readers
    const announcement = document.createElement("div");
    announcement.setAttribute("aria-live", "polite");
    announcement.setAttribute("aria-atomic", "true");
    announcement.className = "sr-only";
    announcement.textContent = `${fuelType.charAt(0).toUpperCase() + fuelType.slice(1)} selected for quote`;
    document.body.appendChild(announcement);

    setTimeout(() => {
      if (announcement.parentNode === document.body) {
        announcement.remove();
      }
    }, 1000);
  };

  return (
    <section
      className={styles.container}
      aria-labelledby="fuel-selector-heading"
    >
      <h2 id="fuel-selector-heading" className={styles.title}>
        You may find it helpful to have a recent energy bill to hand as we'll
        need some details to give you an account quote.
      </h2>
      <br />

      <h2 className={styles.title}>Which fuel would you like a quote for?</h2>

      <fieldset className={styles.fuelTabs}>
        <legend className="sr-only">Select fuel type for your quote</legend>

        <div className={styles.radioWrapper}>
          <input
            type="radio"
            id="fuel-electricity"
            name="fuelType"
            value="electricity"
            checked={selectedFuel === "electricity"}
            onChange={(e) => handleFuelChange("electricity", e)}
            className={styles.radioInput}
            aria-describedby="electricity-description"
          />
          <label
            htmlFor="fuel-electricity"
            className={`${styles.fuelTab} ${
              selectedFuel === "electricity"
                ? `${styles.fuelTabActive} ${styles.electricity}`
                : styles.fuelTabInactive
            }`}
          >
            <div className={styles.fuelTabContent}>
              <div className={styles.fuelTabLeft}>
                <div
                  className={`${styles.fuelIcon} ${
                    selectedFuel === "electricity"
                      ? `${styles.fuelIconActive} ${styles.electricity}`
                      : `${styles.fuelIconInactive} ${styles.electricity}`
                  }`}
                  aria-hidden="true"
                >
                  <Zap className={styles.icon} aria-hidden="true" />
                </div>
                <span className={styles.fuelLabel}>Electricity</span>
              </div>

              <CheckCircle
                className={`${styles.checkIcon} ${
                  selectedFuel === "electricity" ? "" : styles.hidden
                }`}
                aria-hidden="true"
              />
            </div>
            <span id="electricity-description" className="sr-only">
              Select electricity for your business energy quote
            </span>
          </label>
        </div>

        <div className={styles.radioWrapper}>
          <input
            type="radio"
            id="fuel-gas"
            name="fuelType"
            value="gas"
            checked={selectedFuel === "gas"}
            onChange={(e) => handleFuelChange("gas", e)}
            className={styles.radioInput}
            aria-describedby="gas-description"
          />
          <label
            htmlFor="fuel-gas"
            className={`${styles.fuelTab} ${
              selectedFuel === "gas"
                ? `${styles.fuelTabActive} ${styles.gas}`
                : styles.fuelTabInactive
            }`}
          >
            <div className={styles.fuelTabContent}>
              <div className={styles.fuelTabLeft}>
                <div
                  className={`${styles.fuelIcon} ${
                    selectedFuel === "gas"
                      ? `${styles.fuelIconActive} ${styles.gas}`
                      : `${styles.fuelIconInactive} ${styles.gas}`
                  }`}
                  aria-hidden="true"
                >
                  <Droplet className={styles.icon} aria-hidden="true" />
                </div>
                <span className={styles.fuelLabel}>Gas</span>
              </div>
              <CheckCircle
                className={`${styles.checkIcon} ${
                  selectedFuel === "gas" ? "" : styles.hidden
                }`}
                aria-hidden="true"
              />
            </div>
            <span id="gas-description" className="sr-only">
              Select gas for your business energy quote
            </span>
          </label>
        </div>
      </fieldset>
    </section>
  );
};

FuelSelector.propTypes = {
  selectedFuel: PropTypes.string.isRequired,
  onFuelChange: PropTypes.func.isRequired,
};

export default FuelSelector;
